import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { App, Button, Input, Select, Modal } from 'antd';
import { PlusOutlined, CopyOutlined, QrcodeOutlined, ExportOutlined, DownloadOutlined, LinkOutlined } from '@ant-design/icons';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useTranslation } from 'react-i18next';
import api, { errorCode } from './api';
import detectLinkType from './detectLinkType';
import LinkRow, { LINK_TYPES } from './LinkRow';
import BlockRow, { BLOCK_ICONS } from './BlockRow';
import { BLOCK_EDITORS, isBlockValid } from './BlockEditors';
import { BLOCK_KINDS, emptyHours } from '../utils/blocks';
import QrCode from '../components/QrCode';
import SocialIcon from '../components/SocialIcon';
import { PUBLIC_HTTPS_URL, enums } from '../config';

const ShareBar = ({ username }) => {
  const [t] = useTranslation();
  const { message } = App.useApp();
  const [qrOpen, setQrOpen] = useState(false);
  const url = `${PUBLIC_HTTPS_URL}/${username}`;

  const downloadQr = () => {
    const canvas = document.getElementById('qr-gen');
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `piyanos-link-${username}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="dash-share">
      <a className="dash-share__url" href={`/${username}`} target="_blank" rel="noopener noreferrer">
        {url.replace(/^https?:\/\//, '')}
      </a>
      <div className="dash-share__actions">
        <CopyToClipboard text={url} onCopy={() => message.success(t('copied'))}>
          <Button icon={<CopyOutlined />}>{t('copy')}</Button>
        </CopyToClipboard>
        <Button icon={<QrcodeOutlined />} onClick={() => setQrOpen(true)}>{t('dash.qr')}</Button>
        <Button icon={<ExportOutlined />} href={`/${username}`} target="_blank" rel="noopener noreferrer" aria-label={t('dash.viewPage')} title={t('dash.viewPage')} />
      </div>
      <Modal open={qrOpen} onCancel={() => setQrOpen(false)} title={t('dash.qrTitle')} centered width={360}
        footer={<Button type="primary" icon={<DownloadOutlined />} onClick={downloadQr}>{t('dash.qrDownload')}</Button>}>
        <p className="dash-muted">{t('dash.qrHint')}</p>
        {/* White quiet zone: QR scanners need contrast whatever the theme */}
        <div className="dash-qr"><QrCode value={url} /></div>
      </Modal>
    </div>
  );
};

const AddLinkForm = ({ username, save, onAdded }) => {
  const [t] = useTranslation();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState('custom');
  const [typeTouched, setTypeTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setUrl(''); setTitle(''); setType('custom'); setTypeTouched(false); setError(''); setOpen(false);
  };

  const changeUrl = (value) => {
    setUrl(value);
    setError('');
    if (!typeTouched) setType(detectLinkType(value));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!url.trim()) return setError(t('inputErrors.requiredUrl'));
    // An empty title falls back to the platform's name, which is what most
    // people would type anyway
    const linkTitle = title.trim() || (type !== 'custom' ? t(`linkTypes.${type}`) : url.trim().replace(/^https?:\/\//, '').split('/')[0]);
    setSaving(true);
    save(api.addLink(username, { url: url.trim(), linkTitle, type }))
      .then(user => {
        reset();
        const added = user.links[user.links.length - 1];
        if (added) onAdded(added._id);
      })
      .catch(err => setError(errorCode(err) === enums.errorCodes.c13 ? t('inputErrors.invalidUrl') : t('dash.saveFailed')))
      .finally(() => setSaving(false));
  };

  if (!open) {
    return (
      <Button className="dash-add__open" type="primary" size="large" block icon={<PlusOutlined />} onClick={() => setOpen(true)}>
        {t('dash.addLink')}
      </Button>
    );
  }

  return (
    <form className="dash-card dash-add" onSubmit={submit}>
      <label className="dash-field">
        <span>{t('url')}</span>
        <Input autoFocus value={url} onChange={e => changeUrl(e.target.value)} placeholder={t('dash.urlPlaceholder')} status={error ? 'error' : ''}
          inputMode="url" autoCapitalize="none" autoCorrect="off" spellCheck={false} aria-invalid={!!error} />
        {error && <span className="dash-error" role="alert">{error}</span>}
      </label>
      <div className="dash-add__row">
        <label className="dash-field">
          <span>{t('title')}</span>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={type !== 'custom' ? t(`linkTypes.${type}`) : t('dash.titlePlaceholder')} />
        </label>
        <label className="dash-field dash-field--type">
          <span>{t('dash.type')}</span>
          <Select value={type} onChange={value => { setType(value); setTypeTouched(true); }}
            options={LINK_TYPES.map(value => ({ value, label: <span className="dash-type-option"><SocialIcon type={value} />{t(`linkTypes.${value}`)}</span> }))} />
        </label>
      </div>
      <div className="dash-add__actions">
        <Button onClick={reset}>{t('dash.cancel')}</Button>
        <Button type="primary" htmlType="submit" loading={saving}>{t('dash.add')}</Button>
      </div>
    </form>
  );
};

const newBlockData = {
  hours: emptyHours,
  location: () => ({ address: '', showMap: false }),
  menu: () => ({ items: [{ name: '', description: '', price: null }] })
};

// Picks a block kind, then shows its editor until it is added
const AddBlock = ({ username, save, onAdded }) => {
  const [t] = useTranslation();
  const { message } = App.useApp();
  const [kind, setKind] = useState(null);
  const [title, setTitle] = useState('');
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const start = (value) => { setKind(value); setTitle(t(`blocks.defaultTitle.${value}`)); setData(newBlockData[value]()); };
  const reset = () => { setKind(null); setData(null); };

  const submit = () => {
    setSaving(true);
    save(api.addLink(username, { kind, linkTitle: title.trim(), data }))
      .then(user => { reset(); const added = user.links[user.links.length - 1]; if (added) onAdded(added._id); })
      .catch(() => message.error(t('dash.saveFailed')))
      .finally(() => setSaving(false));
  };

  if (!kind) {
    return (
      <div className="dash-blocks">
        <span className="dash-muted">{t('blocks.forBusiness')}</span>
        <div className="dash-blocks__buttons">
          {BLOCK_KINDS.map(value => {
            const Icon = BLOCK_ICONS[value];
            return <Button key={value} icon={<Icon />} onClick={() => start(value)}>{t(`blocks.defaultTitle.${value}`)}</Button>;
          })}
        </div>
      </div>
    );
  }

  const Editor = BLOCK_EDITORS[kind];
  const Icon = BLOCK_ICONS[kind];
  return (
    <div className="dash-card dash-add">
      <h2 className="dash-add__title"><span className="dash-link__icon dash-link__icon--block"><Icon /></span>{t(`blocks.add.${kind}`)}</h2>
      <label className="dash-field">
        <span>{t('blocks.blockTitle')}</span>
        <Input value={title} maxLength={80} onChange={e => setTitle(e.target.value)} />
      </label>
      <Editor value={data} onChange={setData} />
      <div className="dash-add__actions">
        <Button onClick={reset}>{t('dash.cancel')}</Button>
        <Button type="primary" loading={saving} disabled={!isBlockValid(kind, data) || !title.trim()} onClick={submit}>{t('dash.add')}</Button>
      </div>
    </div>
  );
};

const LinksPanel = ({ user, setUser, save }) => {
  const [t] = useTranslation();
  const { message } = App.useApp();
  const [expanded, setExpanded] = useState(null);
  const links = [...user.links].sort((a, b) => a.order - b.order);

  const onDragEnd = ({ source, destination }) => {
    if (!destination || destination.index === source.index) return;
    const next = [...links];
    const [moved] = next.splice(source.index, 1);
    next.splice(destination.index, 0, moved);
    // Show the new order at once; the server's answer replaces it, and a
    // failure puts the old one back
    const previous = user;
    setUser({ ...user, links: next.map((link, index) => ({ ...link, order: index })) });
    save(api.reorder(user.username, next.map(link => link._id))).catch(() => {
      setUser(previous);
      message.error(t('dash.saveFailed'));
    });
  };

  return (
    <div className="dash-panel">
      <header className="dash-panel__head">
        <h1>{t('dash.nav.links')}</h1>
      </header>
      <ShareBar username={user.username} />
      <AddLinkForm username={user.username} save={save} onAdded={setExpanded} />
      <AddBlock username={user.username} save={save} onAdded={setExpanded} />

      {links.length === 0 ? (
        <div className="dash-empty">
          <span className="dash-empty__icon" aria-hidden="true"><LinkOutlined /></span>
          <p>{t('dash.emptyTitle')}</p>
          <span className="dash-muted">{t('dash.emptyBody')}</span>
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="links">
            {provided => (
              <ul className="dash-links" ref={provided.innerRef} {...provided.droppableProps}>
                {links.map((link, index) => (
                  <Draggable key={link._id} draggableId={link._id} index={index}>
                    {(drag, snapshot) => (
                      <li ref={drag.innerRef} {...drag.draggableProps} className={snapshot.isDragging ? 'is-dragging' : ''}>
                        {link.kind && link.kind !== 'link' ? <BlockRow
                          link={link}
                          username={user.username}
                          save={save}
                          dragHandleProps={drag.dragHandleProps}
                          expanded={expanded === link._id}
                          onToggle={() => setExpanded(expanded === link._id ? null : link._id)}
                        /> : <LinkRow
                          link={link}
                          username={user.username}
                          save={save}
                          dragHandleProps={drag.dragHandleProps}
                          expanded={expanded === link._id}
                          onToggle={() => setExpanded(expanded === link._id ? null : link._id)}
                        />}
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};

export default LinksPanel;
